<script setup lang="ts">
  import {
    onMounted,
    ref,
    inject,
    type ComputedRef,
    provide,
    computed,
    defineAsyncComponent,
  } from 'vue'
  import { useUserStore, useCommonStore } from '@/shared/store'
  import { FlowWidget } from '@/widgets/Flow'
  import { useHomeUrl } from '@/shared/composables/useHomeUrl'
  import { TravelDirection } from '@/shared/types/common.types'
  import { useI18n } from 'vue-i18n'
  import { AIRALO_TEST_KEY, AIRALO_TEST_VALUES } from '@/shared/constants'
  import { useFeaturesStore } from '@/shared/store/featuresStore'
  import { FlowSuccessLoader } from '@/entities/Flow/components/FlowSuccessLoader'

  const FlowNetworkErrorWidgetQC = defineAsyncComponent(
    () => import('@/widgets/Flow/FlowNetworkErrorWidgetQC/FlowNetworkErrorWidgetQC.vue')
  )
  const SuccessInsuredWidget = defineAsyncComponent(
    () => import('@/widgets/SuccessInsuredWidget/SuccessInsuredWidget.vue')
  )
  const userStore = useUserStore()
  const commonStore = useCommonStore()
  const { getHomeUrl } = useHomeUrl()
  const homeUrl = getHomeUrl()
  const featureStore = useFeaturesStore()
  const isRTL = inject('isRTL') as ComputedRef<boolean>
  const isLoading = ref(true)
  const policyLink = ref('')
  const cyberPolicyLink = ref<string | undefined>()
  const recieptLink = ref('')
  const policyNumber = ref('')
  const email = ref(userStore.email || '')
  const error = ref(false)
  const isUserRegistered = ref(false)
  const earnedBonuses = ref(0)
  const homePromocode = ref('')

  const { locale } = useI18n()
  // DownloadAppBanner component from loyalty-components lib uses this
  provide('locale', locale)

  const createHomePromocode = async () => {
    const promocode = await commonStore.createHomePromocode(policyNumber.value)
    homePromocode.value = promocode || ''
  }

  const getPolicyInformation = async () => {
    isLoading.value = true
    error.value = false

    try {
      const result = await commonStore.getPolicy()
      policyNumber.value = result.policyNo
      policyLink.value = result.link
      cyberPolicyLink.value = result.cyberSecurityWordingsLink
      recieptLink.value = result.combinedReceiptLink || result.receiptlink
      isUserRegistered.value = result.isUserRegistered
      earnedBonuses.value = result.earnedBonuses ?? 0

      if (isTravelHomeOfferBannerEnabled.value) {
        await createHomePromocode()
      }
    } catch {
      error.value = true
    } finally {
      isLoading.value = false
    }
  }

  onMounted(() => {
    getPolicyInformation()
  })

  const handleOnBack = () => (window.location.href = homeUrl)

  const lastFlowDirection = userStore.lastFlowDirection || TravelDirection.OUTBOUND
  const isAiraloBanner = computed(
    () =>
      AIRALO_TEST_VALUES.includes(commonStore.getAbValue(AIRALO_TEST_KEY)) &&
      lastFlowDirection === TravelDirection.OUTBOUND &&
      featureStore.isAiralooEnabled
  )

  const isOoredooBannerEnabled = computed(
    () => lastFlowDirection === TravelDirection.OUTBOUND && featureStore.isOoredooEnabled
  )

  const isTravelHomeOfferBannerEnabled = computed(
    () => lastFlowDirection === TravelDirection.OUTBOUND && featureStore.isTravelHomeOfferEnabled
  )

  const isTravelHomeOfferBannerVisible = computed(
    () => isTravelHomeOfferBannerEnabled.value && !!homePromocode.value
  )

  const isBannerEnabled = computed(
    () =>
      (isAiraloBanner.value || isOoredooBannerEnabled.value) &&
      !isTravelHomeOfferBannerVisible.value
  )
</script>

<template>
  <FlowWidget
    :steps-count="0"
    :is-rtl="isRTL"
    :with-progress-bar="false"
    class="ds-flow-widget"
    :class="{ 'flow-widget--with-banner': isBannerEnabled && !isLoading && !error }"
    @go-back="handleOnBack"
  >
    <FlowSuccessLoader v-if="isLoading" />
    <template v-else>
      <FlowNetworkErrorWidgetQC v-if="error" :home-url="homeUrl" @resolve="getPolicyInformation" />
      <SuccessInsuredWidget
        v-else-if="!error"
        :type-of-travel="lastFlowDirection"
        :email="email"
        :policy-link="policyLink"
        :cyber-policy-link="cyberPolicyLink"
        :policy-number="policyNumber"
        :reciept-link="recieptLink"
        :is-user-registered="isUserRegistered"
        :earned-bonuses="earnedBonuses"
        :is-airalo-banner="isAiraloBanner"
        :is-ooredoo-banner="isOoredooBannerEnabled"
        :is-travel-home-offer-banner="isTravelHomeOfferBannerVisible"
        :home-promocode="homePromocode"
        :is-with-banner="isBannerEnabled"
      />
    </template>
  </FlowWidget>
</template>

<style lang="scss">
  .flow-widget--with-banner {
    .flow-widget__content {
      max-width: 1088px !important;

      @media (width <= 1087px) {
        max-width: 544px !important;
      }
    }
  }
</style>
